import useFollowee from 'hooks/useFollowee'
import useFollowersInfinite from 'hooks/useFollowersInfinite'
import { FC } from 'react'
import { useGetApiV1SearchQuery } from '@wellesley/openapi'
import { cn } from '@wellesley/ui'
import RemoteUserDisclaimer from './RemoteUserDisclaimer'

import getFullUserUrl from 'helpers/getFullUserUrl'
import ConnectionsModal, {
  ConnectionsTabItem,
  ConnectionsTabs,
} from 'components/modals/ConnectionsModal'
import { FollowersModalProps } from './types'
import { useIsOwnProfile } from '@wellesley/user'
import useConnectionsModal from 'components/modals/ConnectionsModal/hooks/useConnectionsModal'

const defaultLabels = {
  followers: 'Followers',
  following: 'Following',
}

const FollowersModal: FC<FollowersModalProps> = ({
  tab = ConnectionsTabs.first,
  user,
  labels = defaultLabels,
  isOpen,
  onClose,
}) => {
  const { activeTab, setActiveTab, searchQuery, setSearchQuery } =
    useConnectionsModal({ tab })

  const isOwnProfile = useIsOwnProfile(user.id)

  const isRemote = !user.local

  const {
    data: followees,
    isLoading: isFolloweesLoading,
    isHidden: isFolloweesHidden,
  } = useFollowee({
    userId: user.id,
    skip: !isOpen,
  })

  const {
    followers,
    isLoading: isFollowersLoading,
    isHidden: isFollowersHidden,
    hasNextPage: hasNextFollowersPage,
    fetchNextPage: fetchNextFollowersPage,
  } = useFollowersInfinite({
    user,
    skip: !isOpen,
  })

  const showLists = isOwnProfile || (!isFolloweesHidden && !isFollowersHidden)

  const isLoading = isFollowersLoading || isFolloweesLoading

  const { data } = useGetApiV1SearchQuery(
    {
      q: searchQuery,
      filter: ['users'],
      followers: activeTab === ConnectionsTabs.first,
      following: activeTab === ConnectionsTabs.second,
    },
    { skip: !searchQuery },
  )

  const followersList = searchQuery ? data?.users : followers
  const followeesList = searchQuery ? data?.users : followees

  // Only enable infinite scroll when not searching
  const firstListInfiniteScroll = !searchQuery
    ? {
        hasNextPage: hasNextFollowersPage,
        onLoadMore: fetchNextFollowersPage,
      }
    : undefined

  return (
    <ConnectionsModal
      activeTab={activeTab}
      user={user}
      onTabChange={(tab) => setActiveTab(tab as ConnectionsTabItem)}
      showLists={showLists}
      firstList={followersList || []}
      secondList={followeesList || []}
      isLoading={isLoading}
      isOpen={isOpen}
      onClose={onClose}
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      tabs={{
        first: {
          label: labels.followers,
          counter: user.stats?.followers || 0,
        },
        second: {
          label: labels.following,
          counter: user.stats?.following || 0,
        },
      }}
      firstListInfiniteScroll={firstListInfiniteScroll}
      renderFooter={() =>
        isRemote ? (
          <RemoteUserDisclaimer userUrl={getFullUserUrl(user)} />
        ) : null
      }
      listClassName={cn(isRemote && 'max-h-[360px] overflow-visible')}
      hiddenListMessage={`The ${labels.followers} and ${labels.following} lists cannot be displayed due to the user's privacy configuration`}
    />
  )
}

export default FollowersModal
